import { Table, addToast, showDialogConfirm } from '@octano/global-ui';
import { TranslationsKeys } from '../../../locales/translations';
import { useDegreeDocumentsColumns } from '../hooks/useDegreeDocumentsColumns';
import { useTranslation } from 'react-i18next';
import { useState, useCallback } from 'react';
import { DegreeProcessDocument } from '../types';
import { updateDegreeDocument } from '../requests';

type DegreeDocumentsSectionProps = {
  studyPlanEnrollmentId: number;
  documents?: DegreeProcessDocument[];
  onRefresh?: () => void;
};

export function DegreeDocumentsSection({
  studyPlanEnrollmentId,
  documents = [],
  onRefresh,
}: DegreeDocumentsSectionProps) {
  const { t } = useTranslation(TranslationsKeys.DEGREE_POOCESSES_DOCUMENTS);

  const [uploadingId, setUploadingId] = useState<number>();
  const [deletingId, setDeletingId] = useState<number>();

  const handleUpload = useCallback(
    async (document: DegreeProcessDocument, fileId: number) => {
      if (uploadingId || deletingId) {
        return;
      }
      setUploadingId(document.id);
      const { error } = await updateDegreeDocument({
        studyPlanEnrollmentId,
        degreeDocumentId: document.id,
        fileId,
      });
      setUploadingId(undefined);
      if (!error) {
        !!onRefresh && onRefresh();
        addToast({
          color: 'success',
          icon: 'check',
          text: t('successUploaded', { document: document.name }),
        });
      } else {
        addToast({
          color: 'danger',
          icon: 'error',
          text: t('failedUploaded', { document: document.name }),
        });
      }
    },
    [uploadingId, deletingId, studyPlanEnrollmentId, onRefresh, t],
  );

  const handleDelete = useCallback(
    async (document: DegreeProcessDocument) => {
      if (uploadingId || deletingId) {
        return;
      }
      setDeletingId(document.id);
      const { error } = await updateDegreeDocument({
        studyPlanEnrollmentId,
        degreeDocumentId: document.id,
        fileId: null,
      });
      setDeletingId(undefined);
      if (!error) {
        !!onRefresh && onRefresh();
        addToast({
          color: 'success',
          icon: 'check',
          text: t('successRemoved', { document: document.name }),
        });
      } else {
        addToast({
          color: 'danger',
          icon: 'error',
          text: t('failedRemoved', { document: document.name }),
        });
      }
    },
    [uploadingId, deletingId, studyPlanEnrollmentId, onRefresh, t],
  );

  const handleRequestDelete = useCallback(
    async (document: DegreeProcessDocument) => {
      if (uploadingId || deletingId) {
        return;
      }
      showDialogConfirm({
        icon: {
          name: 'warning',
          color: 'warning',
        },
        title: t(`confirmDeleteTitle`, { document: document.name }),
        subtitle: t(`confirmDeleteDescription`, { document: document.name }),
        btnConfirm: {
          text: t(`confirmDeleteAgree`),
          onConfirm: () => handleDelete(document),
        },
        btnClose: {
          text: t(`confirmDeleteCancel`),
        },
      });
    },
    [uploadingId, deletingId, handleDelete, t],
  );

  const columns = useDegreeDocumentsColumns({
    uploadingId,
    deletingId,
    onDelete: handleRequestDelete,
    onUpload: handleUpload,
  });

  return (
    <>
      <h2 className="fs-20 fw-600 text-primary text-uppercase mb-4">
        {t('title')}
      </h2>
      <Table columns={columns} data={documents} />
    </>
  );
}
